import { RefObject, useRef } from 'react'

/**
 * A ref for Chakra's focus-targeting props — `initialFocusRef`, `finalFocusRef`,
 * and `leastDestructiveRef` on `Modal` / `AlertDialog` / `Popover` (and the
 * design-system modals that wrap them).
 *
 * **Why this exists:** Chakra v2 types those props as `RefObject<FocusableElement>`
 * (non-nullable `current`). React 19's `useRef<T>(null)` resolves to
 * `RefObject<T | null>`, and the `| null` makes it unassignable to Chakra's stricter
 * type — even though it's exactly the ref Chakra expects at runtime. React's own
 * `ref={}` prop accepts the nullable form via special handling; Chakra's plain props
 * do not.
 *
 * Rather than scatter `as RefObject<T>` casts across every modal, this hook contains
 * the single, intentional cast in one place. The returned ref is still element-typed,
 * so attaching it (`ref={focusRef}`) stays type-checked.
 *
 * **Remove this** when Chakra's focus-ref prop types accept nullable refs (Chakra v3
 * or a React-19 types fix) — at which point `useRef<T>(null)` can be used directly.
 *
 * @example
 * const cancelRef = useChakraFocusRef<HTMLButtonElement>()
 * <AlertDialog leastDestructiveRef={cancelRef}>…</AlertDialog>
 */
export function useChakraFocusRef<T extends HTMLElement = HTMLElement>(): RefObject<T> {
  return useRef<T>(null) as RefObject<T>
}
